// ─── Credit Card Reconciliation — TypeScript Interfaces ──────────────────────
// Mirrors cc_recon_sessions + cc_recon_transactions DB schema (PLInvoiceProd).
// CC Helper mapping mirrors cc_helper_mapping (PLInvoiceSettings).

// ── Session ───────────────────────────────────────────────────────────────────

export interface CCReconSession {
  id: number;
  session_name: string;
  period_start: string | null;       // ISO date string "YYYY-MM-DD"
  period_end: string | null;         // ISO date string "YYYY-MM-DD"
  uploaded_by: string;               // Entra email
  uploaded_at: string;               // ISO timestamp
  status: 'processing' | 'open' | 'complete' | 'error';
  completed_at: string | null;       // ISO timestamp, set on complete
  total_transactions: number;
  total_matched: number;             // running decimal — Converted + Confirmed Match amounts
  total_unmatched: number;           // decimal — remaining to resolve
  cc_vendor_number: number | null;   // WinTeam creditCardVendorNumber for this session; null = use system setting
  beginning_balance: number | null;  // statement beginning balance (user-entered, for recon report)
  ending_balance: number | null;     // statement ending balance (user-entered, for recon report)
  invoice_seq_start: number;         // starting XXXX for CC{vendor}-{YYMM}-{XXXX} invoice numbers (default 1)
  finance_charge: number | null;     // statement finance charge (auto-filled from parser, editable)
  fee_charged: number | null;        // statement fees (auto-filled from parser, editable)
}

// ── Credit Card ───────────────────────────────────────────────────────────────

/** Row from ap_credit_cards — used in the upload modal dropdown. */
export interface CreditCard {
  id: number;
  cc_vendor_number: number;
  description: string;
  is_default: boolean;
  active: boolean;
}

// ── Transaction ───────────────────────────────────────────────────────────────

export type CCTransactionType = 'Sale' | 'Return' | 'Payment' | 'Fee' | 'FinanceCharge';

export type CCResolutionSource =
  | 'CC Helper'
  | 'CC Keyword'       // Session 56: contains match (replaces Fuzzy Helper)
  | 'Fuzzy Helper'     // Legacy — kept for existing session data in DB
  | 'Vendor Match'
  | 'GL Keyword'
  | 'AI Assist'
  | 'Unresolved'
  | 'Manual';

export type CCTransactionStatus =
  | 'Unresolved'       // GL 5555 — blocked from conversion, highlighted yellow
  | 'Coded'            // auto-resolved (CC Helper / Vendor Match / AI Assist)
  | 'Corrected'        // user manually edited vendor/GL/job
  | 'Possible Match'   // potential WinTeam match found (Step 9)
  | 'Confirmed Match'  // user confirmed already in WinTeam — excluded from POST
  | 'Converted'        // successfully POSTed to WinTeam as invoice
  | 'Duplicate'        // detected as duplicate within session or WinTeam
  | 'Error'            // API POST failed — retry available
  | 'Payment'          // statement payment — tracked for the recon report only, never coded/converted
  | 'Fee'              // statement fee — report-only
  | 'Finance Charge';  // statement finance charge / interest — report-only

export interface CCReconTransaction {
  id: number;
  session_id: number;
  card_last4: string;
  transaction_date: string;          // ISO date "YYYY-MM-DD"
  post_date: string;                 // ISO date "YYYY-MM-DD"
  description: string;               // raw Chase description
  category: string;                  // Chase category (Travel, Food & Drink, etc.)
  type: CCTransactionType;
  amount: number;                    // raw Chase amount (negative = charge)
  invoice_amount: number;            // positive WinTeam amount
  vendor_number: number;
  gl_number: number;
  job_number: string;
  resolution_source: CCResolutionSource;
  status: CCTransactionStatus;
  winteam_invoice_number: string | null;   // set after successful WinTeam POST
  matched_invoice_number: string | null;   // set after Confirmed Match
  notes: string | null;
  suggested_keyword: string | null;   // AI-generated or user-edited keyword for future CC Helper learning
  created_at: string;
  updated_at: string;
}

// ── CC Helper Mapping ─────────────────────────────────────────────────────────

export type CCHelperSource = 'manual' | 'seeded' | 'ai_confirmed';

export interface CCHelperMapping {
  id: number;
  description_pattern: string;       // normalized: UPPER(TRIM(description))
  vendor_number: number;
  gl_number: number;
  job_number: string;
  match_count: number;
  last_used: string | null;          // ISO date
  source: CCHelperSource;
  created_at: string;
}

// ── Parse Summary (returned from POST /sessions) ──────────────────────────────

export interface CCParseSummary {
  total_rows: number;
  excluded_payments: number;
  parsed: number;
  period_start: string | null;
  period_end: string | null;
  parse_errors: string[];
}

// ── API Response shapes ───────────────────────────────────────────────────────

export interface CCCreateSessionResponse {
  session: CCReconSession;
  parse_summary: CCParseSummary;
}

export interface CCGetSessionResponse {
  session: CCReconSession;
  transactions: CCReconTransaction[];
}

export interface CCConvertAllResponse {
  converted: Array<{ id: number; invoice_number: string }>;
  errors:    Array<{ id: number; error: string }>;
}

// ── Request body shapes ───────────────────────────────────────────────────────

export interface CCTransactionUpdate {
  vendor_number?: number;
  gl_number?: number;
  job_number?: string;
  notes?: string;
  suggested_keyword?: string;
}

export interface CCHelperMappingUpsert {
  description_pattern: string;
  vendor_number: number;
  gl_number: number;
  job_number?: string;               // defaults to "11" if omitted
  source?: CCHelperSource;           // defaults to "manual" if omitted
}

// ── Possible Match — WinTeam candidate invoice ────────────────────────────────

/** GL distribution line from a WinTeam AP invoice. */
export interface CCWinTeamGLDistribution {
  account_number: number;
  job_number: string;
  amount: number;
  notes: string;
}

/** A WinTeam AP invoice returned as a candidate for Possible Match review. */
export interface CCWinTeamInvoiceCandidate {
  invoice_number: string;
  vendor_number: number;
  invoice_amount: number;
  invoice_date: string;                    // ISO date "YYYY-MM-DD"
  posting_date: string;                    // ISO date "YYYY-MM-DD"
  due_date: string;                        // ISO date "YYYY-MM-DD"
  company_number: number;
  payment_method_id: number;               // normalized numeric ID; CreditCard = 2
  payment_method: string;                  // e.g. "2" or "CreditCard"
  credit_card_vendor_number: number;
  memo_line1: string | null;
  gl_distributions: CCWinTeamGLDistribution[];
}

/** Response from GET /cc-recon/transactions/{id}/matches */
export interface CCMatchesResponse {
  transaction: CCReconTransaction;
  candidates: CCWinTeamInvoiceCandidate[];
}

/** Response from POST /cc-recon/sessions/{id}/find-matches */
export interface CCFindMatchesResponse {
  checked: number;
  updated: number;
}

// ── UI helpers ────────────────────────────────────────────────────────────────

/** Statuses that block the user from converting a transaction to WinTeam. */
export const CC_BLOCKED_STATUSES: CCTransactionStatus[] = [
  'Unresolved',
  'Possible Match',
  'Confirmed Match',
  'Converted',
  'Duplicate',
  'Payment',
  'Fee',
  'Finance Charge',
];

/** Statuses that prevent any inline editing of a transaction row. */
export const CC_LOCKED_STATUSES: CCTransactionStatus[] = [
  'Converted',
  'Confirmed Match',
  'Duplicate',
  'Payment',
  'Fee',
  'Finance Charge',
];

/** Statement-payment status. @deprecated use CC_REPORT_ONLY_STATUSES. */
export const CC_PAYMENT_STATUS: CCTransactionStatus = 'Payment';

/** Statuses that are report/totals-only (payments, fees, finance charges).
 *  These rows are filtered out of every working tab in the transaction grid. */
export const CC_REPORT_ONLY_STATUSES: CCTransactionStatus[] = [
  'Payment',
  'Fee',
  'Finance Charge',
];

/** Unresolved highlight color — yellow, matches CODED invoice status palette. */
export const CC_UNRESOLVED_COLOR = '#fff8e1';

/** GL number that marks a transaction as Unresolved and blocks conversion. */
export const CC_UNRESOLVED_GL = 5555;

